/**
 * TakeSurveyPages - Component for taking surveys with page-based structure
 * 
 * Supports:
 * - Multiple pages with questions
 * - Page-based logic (skip to page X if answer Y)
 * - Progress tracking
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  LinearProgress,
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox,
  TextField,
  Alert,
  FormGroup,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Slider
} from '@mui/material';
import { Delete as DeleteIcon, CloudUpload as UploadIcon } from '@mui/icons-material';
import axios from 'axios';
import { getShuffledOptions, getShuffledImageOptions } from '../../utils/shuffleOptions';
import {
  selectionHint,
  selectionProgressMessage,
  multiSelectBlocksProgress,
  isSelectionCapped,
} from '../../utils/multiSelectLimit';
import { matrixBlocksProgress, matrixProgressMessage, getMissingMatrixRows } from '../../utils/matrixCompletion';
import MatrixQuestion from '../../components/MatrixQuestion';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const SERVER_BASE = API_BASE.replace('/api', '');

export default function TakeSurveyPages({ testMode = false, surveyId = null }) {
  const { id: paramId } = useParams();
  const id = surveyId || paramId; // Use prop if provided, otherwise use URL param
  const navigate = useNavigate();
  
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Page navigation
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [pageHistory, setPageHistory] = useState([0]); // For back button
  
  // Answers
  const [answers, setAnswers] = useState({});
  const [files, setFiles] = useState({}); // Store actual file objects for upload
  
  // Submission
  const [submitting, setSubmitting] = useState(false);
  const [startTime] = useState(Date.now());
  
  // Validation
  const [attemptedNext, setAttemptedNext] = useState(false);
  
  useEffect(() => {
    fetchSurvey();
  }, [id]);
  
  const fetchSurvey = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/surveys/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const surveyData = response.data;
      
      // Ensure pages exist (backward compatibility)
      if (!surveyData.pages || surveyData.pages.length === 0) {
        // Create default page with all questions
        surveyData.pages = [{
          id: 'page-1',
          title: 'Страница 1',
          questionIds: (surveyData.questions || []).map(q => q.id)
        }];
      }
      
      setSurvey(surveyData);
      setError('');
    } catch (err) {
      console.error('Error fetching survey:', err);
      setError('Ошибка загрузки опроса');
    } finally {
      setLoading(false);
    }
  };
  
  // Get current page
  const getCurrentPage = () => {
    if (!survey || !survey.pages) return null;
    return survey.pages[currentPageIndex] || null;
  };
  
  // Get questions for current page
  const getCurrentPageQuestions = () => {
    const page = getCurrentPage();
    if (!page || !survey.questions) return [];
    
    return page.questionIds
      .map(qId => survey.questions.find(q => q.id === qId))
      .filter(Boolean);
  };
  
  // Check if all questions on current page are answered
  const isCurrentPageComplete = () => {
    const questions = getCurrentPageQuestions();
    return questions.every(q => {
      if (multiSelectBlocksProgress(q, answers[q.id])) return false;
      if (matrixBlocksProgress(q, answers[q.id])) return false;
      if (!q.required) return true;
      
      // For file questions, check if file is uploaded
      if (q.type === 'file') {
        // In test mode, skip file upload validation
        if (testMode) return true;
        return !!files[q.id];
      }
      
      const answer = answers[q.id];
      if (!answer) return false;
      if (Array.isArray(answer)) {
        // For arrays, filter out __custom__ marker and check if there's real content
        const realAnswers = answer.filter(a => a !== '__custom__');
        return realAnswers.length > 0;
      }
      // For strings, check if it's not empty and not just the __custom__ marker
      const trimmed = String(answer).trim();
      return trimmed !== '' && trimmed !== '__custom__';
    });
  };

  // Check if a specific question is required and unanswered
  const isQuestionUnanswered = (question) => {
    if (multiSelectBlocksProgress(question, answers[question.id])) return true;
    if (matrixBlocksProgress(question, answers[question.id])) return true;
    if (!question.required) return false;
    
    // For file questions, check if file is uploaded
    if (question.type === 'file') {
      // In test mode, skip file upload validation
      if (testMode) return false;
      return !files[question.id];
    }
    
    const answer = answers[question.id];
    
    // Check if answer is empty
    if (answer === undefined || answer === null || answer === '') {
      return true;
    }
    
    // Check if it's just the __custom__ marker without real content
    if (answer === '__custom__') {
      return true;
    }
    
    // Check if array answer is empty or contains only __custom__ marker
    if (Array.isArray(answer)) {
      const realAnswers = answer.filter(a => a !== '__custom__');
      return realAnswers.length === 0;
    }
    
    // Check if string answer is empty
    if (typeof answer === 'string' && answer.trim() === '') {
      return true;
    }
    
    return false;
  };
  
  // Handle answer change
  const handleAnswerChange = (questionId, value) => {
    setAnswers({
      ...answers,
      [questionId]: value
    });
    // Reset validation error when user starts answering
    if (attemptedNext) {
      setAttemptedNext(false);
    }
  };
  
  // Handle file upload
  const handleFileUpload = (questionId, file) => {
    if (!file) return;
    
    const maxSize = 15 * 1024 * 1024; // 15MB in bytes
    if (file.size > maxSize) {
      alert('Размер файла не должен превышать 15МБ');
      return;
    }
    
    setFiles(prev => ({ ...prev, [questionId]: file }));
    // Reset validation error when user uploads a file
    if (attemptedNext) {
      setAttemptedNext(false);
    }
  };
  
  // Apply page logic to determine next page
  const getNextPageIndex = () => {
    if (!survey.pageLogic || survey.pageLogic.length === 0) {
      // No logic, just go to next page
      return { nextIndex: currentPageIndex + 1, reason: null };
    }
    
    const currentPage = getCurrentPage();
    if (!currentPage) return { nextIndex: currentPageIndex + 1, reason: null };
    
    // Check if any logic rule applies
    for (const rule of survey.pageLogic) {
      // Check if the question is on the current page
      if (!currentPage.questionIds.includes(rule.questionId)) continue;
      
      const answer = answers[rule.questionId];
      if (!answer) continue;
      
      // Check if condition matches
      const conditionAnswers = rule.condition?.answers || (rule.condition?.answer ? [rule.condition.answer] : []);
      if (!conditionAnswers || conditionAnswers.length === 0) continue;
      
      // Match answer with condition
      let matches = false;
      if (Array.isArray(answer)) {
        // Multiple choice: check if any selected answer matches
        matches = answer.some(ans => conditionAnswers.includes(ans));
      } else {
        // Single choice: check if answer matches
        matches = conditionAnswers.includes(String(answer));
      }
      
      if (matches) {
        // Check action type
        if (rule.action === 'end') {
          // End survey immediately
          return { nextIndex: survey.pages.length, reason: null }; // This will trigger submit
        } else if (rule.action === 'endWithoutPayment') {
          // End survey without payment (disqualified)
          return { nextIndex: -1, reason: rule.disqualificationReason || null }; // Special value to indicate disqualification
        } else if (rule.action === 'goToPage' && rule.targetPageId) {
          // Go to specific page
          const targetIndex = survey.pages.findIndex(p => p.id === rule.targetPageId);
          if (targetIndex >= 0) {
            return { nextIndex: targetIndex, reason: null };
          }
        }
      }
    }
    
    // No logic matched, go to next page
    return { nextIndex: currentPageIndex + 1, reason: null };
  };
  
  // Handle next page
  const handleNext = async () => {
    console.log('handleNext called, currentPageIndex:', currentPageIndex, 'total pages:', survey.pages.length);
    
    setAttemptedNext(true);
    
    if (!isCurrentPageComplete()) {
      console.log('Current page is not complete');
      // Scroll to the first incomplete question (fallback: top of the page)
      const firstIncomplete = getCurrentPageQuestions().find(q => isQuestionUnanswered(q));
      const el = firstIncomplete && document.getElementById(`question-${firstIncomplete.id}`);
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return;
    }
    
    setError('');
    setAttemptedNext(false);
    
    const { nextIndex, reason } = getNextPageIndex();
    console.log('Next page index:', nextIndex);
    
    // Check if disqualified (endWithoutPayment)
    if (nextIndex === -1) {
      console.log('Survey ended without payment (disqualified)');
      if (testMode) {
        alert(`Тестирование завершено! Респондент был дисквалифицирован (завершение без оплаты).${reason ? `\n\nПричина: ${reason}` : ''}`);
        navigate('/client/dashboard');
      } else {
        // Submit answers with disqualified flag, then redirect
        await handleSubmitDisqualified(reason);
      }
      return;
    }
    
    // Check if last page
    if (nextIndex >= survey.pages.length) {
      console.log('Last page reached, calling handleSubmit');
      handleSubmit();
      return;
    }
    
    // Navigate to next page
    setPageHistory([...pageHistory, currentPageIndex]);
    setCurrentPageIndex(nextIndex);
    // Scroll to top when moving to next page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Handle back
  const handleBack = () => {
    if (pageHistory.length <= 1) return;
    
    const newHistory = [...pageHistory];
    const previousIndex = newHistory.pop();
    
    setPageHistory(newHistory);
    setCurrentPageIndex(previousIndex);
    setError('');
    setAttemptedNext(false);
    // Scroll to top when going back
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Submit survey with disqualified flag
  const handleSubmitDisqualified = async (reason) => {
    console.log('handleSubmitDisqualified called, reason:', reason);
    
    setSubmitting(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const timeSpent = Math.floor((Date.now() - startTime) / 1000);
      
      // Upload all files first and get their URLs
      const answersWithFiles = { ...answers };
      
      for (const [questionId, file] of Object.entries(files)) {
        if (file) {
          const formData = new FormData();
          formData.append('file', file);
          
          try {
            const uploadResponse = await axios.post(`${API_BASE}/upload/survey-response`, formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
                Authorization: `Bearer ${token}`
              }
            });
            
            answersWithFiles[questionId] = {
              fileUrl: uploadResponse.data.fileUrl,
              originalName: uploadResponse.data.originalName,
              size: uploadResponse.data.size,
              mimetype: uploadResponse.data.mimetype
            };
          } catch (uploadErr) {
            console.error('Error uploading file:', uploadErr);
          }
        }
      }
      
      console.log('Submitting disqualified survey:', {
        surveyId: id,
        answers: answersWithFiles,
        timeSpent,
        disqualified: true
      });
      
      await axios.post(
        `${API_BASE}/surveys/${id}/responses`,
        {
          answers: answersWithFiles,
          timeSpent,
          disqualified: true,
          disqualificationReason: reason || null
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      console.log('Disqualified survey submitted successfully');
      
      // Redirect to disqualified page
      navigate(`/respondent/surveys/${id}/disqualified`, {
        state: { reason: reason || null }
      });
    } catch (err) {
      console.error('Error submitting disqualified survey:', err);
      // Still redirect to disqualified page even if submission fails
      navigate(`/respondent/surveys/${id}/disqualified`, {
        state: { reason: reason || null }
      });
    } finally {
      setSubmitting(false);
    }
  };
  
  // Submit survey
  const handleSubmit = async () => {
    console.log('handleSubmit called, testMode:', testMode);

    setSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const timeSpent = Math.floor((Date.now() - startTime) / 1000);

      // Upload all files first and get their URLs
      const answersWithFiles = { ...answers };

      for (const [questionId, file] of Object.entries(files)) {
        if (file) {
          const formData = new FormData();
          formData.append('file', file);

          try {
            const uploadResponse = await axios.post(`${API_BASE}/upload/survey-response`, formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
                Authorization: `Bearer ${token}`
              }
            });

            answersWithFiles[questionId] = {
              fileUrl: uploadResponse.data.fileUrl,
              originalName: uploadResponse.data.originalName,
              size: uploadResponse.data.size,
              mimetype: uploadResponse.data.mimetype
            };
          } catch (uploadErr) {
            console.error('Error uploading file:', uploadErr);
            setError(`Ошибка загрузки файла: ${file.name}`);
            setSubmitting(false);
            return;
          }
        }
      }

      if (testMode) {
        // Тест: ответы не сохраняем, но Bitrix — по правилам опроса
        let bitrixMsg = '';
        try {
          const bitrixRes = await axios.post(
            `${API_BASE}/surveys/${id}/test-bitrix`,
            { answers: answersWithFiles },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (bitrixRes.data?.synced) {
            const kind = bitrixRes.data.entityType === 'deal' ? 'сделка' : 'лид';
            bitrixMsg = `\n\nBitrix: ${kind} создан (ID ${bitrixRes.data.entityId}).`;
          } else if (bitrixRes.data?.skipped) {
            bitrixMsg = `\n\nBitrix: не отправлено (${bitrixRes.data.message || bitrixRes.data.reason}).`;
          }
        } catch (bitrixErr) {
          console.error('Test Bitrix sync error:', bitrixErr);
          const details = bitrixErr.response?.data?.details || bitrixErr.response?.data?.error;
          bitrixMsg = `\n\nBitrix: ошибка отправки${details ? `: ${details}` : ''}.`;
        }

        alert(`Тестирование завершено! В реальном режиме ответы были бы сохранены.${bitrixMsg}`);
        navigate('/client/dashboard');
        return;
      }

      console.log('Submitting survey:', {
        surveyId: id,
        answers: answersWithFiles,
        timeSpent
      });

      const response = await axios.post(
        `${API_BASE}/surveys/${id}/responses`,
        {
          answers: answersWithFiles,
          timeSpent
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      console.log('Survey submitted successfully:', response.data);

      navigate('/respondent/dashboard', {
        state: { message: 'Спасибо за прохождение опроса!' }
      });
    } catch (err) {
      console.error('Error submitting survey:', err);
      console.error('Error details:', err.response?.data);
      setError(err.response?.data?.error || 'Ошибка отправки ответов');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Render question based on type
  const renderQuestion = (question) => {
    const answer = answers[question.id];
    
    switch (question.type) {
      case 'single':
        const customSingleKey = '__custom__';
        const isCustomSingle = answer !== undefined && answer !== null && !(question.options || []).includes(answer);
        const displayValueSingle = isCustomSingle ? customSingleKey : (answer || '');
        
        return (
          <Box>
            <RadioGroup
              value={displayValueSingle}
              onChange={(e) => {
                if (e.target.value === customSingleKey) {
                  handleAnswerChange(question.id, customSingleKey);
                } else {
                  handleAnswerChange(question.id, e.target.value);
                }
              }}
            >
              {getShuffledOptions(question, id).map((option, idx) => (
                <FormControlLabel
                  key={idx}
                  value={option}
                  control={<Radio />}
                  label={option}
                />
              ))}
              {question.allowCustomOption && (
                <FormControlLabel
                  value={customSingleKey}
                  control={<Radio />}
                  label="Свой вариант"
                />
              )}
            </RadioGroup>
            {question.allowCustomOption && displayValueSingle === customSingleKey && (
              <TextField
                fullWidth
                size="small"
                placeholder="Введите свой вариант"
                value={answer === customSingleKey ? '' : (answer || '')}
                onChange={(e) => handleAnswerChange(question.id, e.target.value || customSingleKey)}
                sx={{ mt: 1, pl: 4, boxSizing: 'border-box' }}
              />
            )}
          </Box>
        );

      case 'dropdown':
        const customDropdownKey = '__custom__';
        const isCustomDropdown = answer !== undefined && answer !== null && !(question.options || []).includes(answer);
        const displayValueDropdown = isCustomDropdown ? customDropdownKey : (answer || '');
        
        return (
          <Box>
            <FormControl fullWidth>
              <InputLabel>Выберите вариант</InputLabel>
              <Select
                value={displayValueDropdown}
                label="Выберите вариант"
                onChange={(e) => {
                  if (e.target.value === customDropdownKey) {
                    handleAnswerChange(question.id, customDropdownKey);
                  } else {
                    handleAnswerChange(question.id, e.target.value);
                  }
                }}
              >
                {getShuffledOptions(question, id).map((option, idx) => (
                  <MenuItem key={idx} value={option}>
                    {option}
                  </MenuItem>
                ))}
                {question.allowCustomOption && (
                  <MenuItem value={customDropdownKey}>Свой вариант</MenuItem>
                )}
              </Select>
            </FormControl>
            {question.allowCustomOption && displayValueDropdown === customDropdownKey && (
              <TextField
                fullWidth
                size="small"
                placeholder="Введите свой вариант"
                value={answer === customDropdownKey ? '' : (answer || '')}
                onChange={(e) => handleAnswerChange(question.id, e.target.value || customDropdownKey)}
                sx={{ mt: 1 }}
              />
            )}
          </Box>
        );
      
      case 'multiple':
        const answerArray = Array.isArray(answer) ? answer : [];
        const customValues = answerArray.filter(a => !(question.options || []).includes(a));
        const hasCustom = customValues.length > 0;
        const customText = customValues.join(', ');
        const atSelectionCap = isSelectionCapped(question, answerArray);
        
        return (
          <Box>
            <FormGroup>
              {getShuffledOptions(question, id).map((option, idx) => {
                const optionChecked = answerArray.includes(option);
                return (
                <FormControlLabel
                  key={idx}
                  control={
                    <Checkbox
                      checked={optionChecked}
                      disabled={!optionChecked && atSelectionCap}
                      onChange={(e) => {
                        if (e.target.checked && isSelectionCapped(question, answerArray)) return;
                        // Сохраняем кастомные значения отдельно
                        const customAnswers = answerArray.filter(a => !(question.options || []).includes(a));
                        // Получаем текущие выбранные стандартные опции
                        const standardAnswers = answerArray.filter(a => (question.options || []).includes(a));
                        
                        let updated;
                        if (e.target.checked) {
                          // Добавляем выбранную опцию к существующим стандартным + кастомные
                          updated = [...standardAnswers, option, ...customAnswers];
                        } else {
                          // Убираем опцию из стандартных + оставляем кастомные
                          updated = [...standardAnswers.filter(a => a !== option), ...customAnswers];
                        }
                        handleAnswerChange(question.id, updated);
                      }}
                    />
                  }
                  label={option}
                />
                );
              })}
              {question.allowCustomOption && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hasCustom}
                      disabled={!hasCustom && atSelectionCap}
                      onChange={(e) => {
                        if (e.target.checked && isSelectionCapped(question, answerArray)) return;
                        if (e.target.checked) {
                          // Add custom marker
                          const validOptions = answerArray.filter(a => (question.options || []).includes(a));
                          handleAnswerChange(question.id, [...validOptions, '__custom__']);
                        } else {
                          // Remove all custom values
                          const updated = answerArray.filter(a => (question.options || []).includes(a));
                          handleAnswerChange(question.id, updated);
                        }
                      }}
                    />
                  }
                  label="Свой вариант"
                />
              )}
            </FormGroup>
            {question.allowCustomOption && hasCustom && (
              <TextField
                fullWidth
                size="small"
                placeholder="Введите свой вариант"
                value={customValues.filter(v => v !== '__custom__').join(', ')}
                onChange={(e) => {
                  const validOptions = answerArray.filter(a => (question.options || []).includes(a));
                  if (e.target.value.trim()) {
                    handleAnswerChange(question.id, [...validOptions, e.target.value]);
                  } else {
                    handleAnswerChange(question.id, [...validOptions, '__custom__']);
                  }
                }}
                sx={{ mt: 1, pl: 4, boxSizing: 'border-box' }}
              />
            )}
          </Box>
        );
      
      case 'text':
        return (
          <TextField
            fullWidth
            multiline
            rows={3}
            value={answer || ''}
            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
            placeholder="Ваш ответ..."
          />
        );
      
      case 'single-image':
        const imageOptionsSingle = question.imageOptions || [];
        
        if (imageOptionsSingle.length === 0) {
          return (
            <Alert severity="info" variant="outlined">
              <Typography variant="caption">Добавьте варианты с изображениями</Typography>
            </Alert>
          );
        }
        
        const customSingleImgKey = '__custom__';
        const isCustomSingleImg = answer !== undefined && answer !== null && !imageOptionsSingle.find(opt => opt.text === answer);
        const displayValueSingleImg = isCustomSingleImg ? customSingleImgKey : (answer || '');
        
        return (
          <Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5, mb: 2 }}>
              {getShuffledImageOptions(question, id).map((option, idx) => {
                if (!option || !option.text) return null;
                const isSelected = answer === option.text;
                return (
                  <Paper
                    key={idx}
                    elevation={isSelected ? 4 : 1}
                    onClick={() => handleAnswerChange(question.id, option.text)}
                    sx={{
                      p: 1.5,
                      cursor: 'pointer',
                      border: 2,
                      borderColor: isSelected ? 'primary.main' : 'transparent',
                      transition: 'all 0.2s',
                      '&:hover': { 
                        borderColor: 'primary.light',
                        elevation: 3
                      }
                    }}
                  >
                    {option.imageUrl && (
                      <Box
                        component="img"
                        src={option.imageUrl.startsWith('/') ? `${SERVER_BASE}${option.imageUrl}` : option.imageUrl}
                        alt={option.text}
                        sx={{ 
                          width: '100%', 
                          maxHeight: 200, 
                          objectFit: 'contain', 
                          borderRadius: 1, 
                          mb: 1 
                        }}
                      />
                    )}
                    <Typography variant="body2" align="center">
                      {option.text}
                    </Typography>
                  </Paper>
                );
              })}
            </Box>
            {question.allowCustomOption && (
              <FormControlLabel
                control={
                  <Radio
                    size="small"
                    checked={displayValueSingleImg === customSingleImgKey}
                    onChange={() => {
                      handleAnswerChange(question.id, customSingleImgKey);
                    }}
                  />
                }
                label={<Typography variant="body2">Свой вариант</Typography>}
              />
            )}
            {question.allowCustomOption && displayValueSingleImg === customSingleImgKey && (
              <TextField
                fullWidth
                size="small"
                placeholder="Введите свой вариант"
                value={answer === customSingleImgKey ? '' : (answer || '')}
                onChange={(e) => handleAnswerChange(question.id, e.target.value || customSingleImgKey)}
                sx={{ mt: 1 }}
              />
            )}
          </Box>
        );

      case 'multiple-image':
        const imageOptionsMultiple = question.imageOptions || [];
        
        if (imageOptionsMultiple.length === 0) {
          return (
            <Alert severity="info" variant="outlined">
              <Typography variant="caption">Добавьте варианты с изображениями</Typography>
            </Alert>
          );
        }
        
        const answerArrayImg = Array.isArray(answer) ? answer : [];
        const customValuesImg = answerArrayImg.filter(a => !imageOptionsMultiple.find(opt => opt.text === a));
        const hasCustomImg = customValuesImg.length > 0;
        const customTextImg = customValuesImg.join(', ');
        const atImageSelectionCap = isSelectionCapped(question, answerArrayImg);
        
        return (
          <Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5, mb: 2 }}>
              {getShuffledImageOptions(question, id).map((option, idx) => {
                if (!option || !option.text) return null;
                const isSelected = answerArrayImg.includes(option.text);
                const imageBlocked = !isSelected && atImageSelectionCap;
                return (
                  <Paper
                    key={idx}
                    elevation={isSelected ? 4 : 1}
                    onClick={() => {
                      if (imageBlocked) return;
                      const currentAnswers = answerArrayImg;
                      const validAnswers = currentAnswers.filter(a => imageOptionsMultiple.find(opt => opt.text === a) || customValuesImg.includes(a));
                      const newAnswers = isSelected
                        ? validAnswers.filter(a => a !== option.text)
                        : [...validAnswers, option.text];
                      handleAnswerChange(question.id, newAnswers);
                    }}
                    sx={{
                      p: 1.5,
                      cursor: imageBlocked ? 'not-allowed' : 'pointer',
                      opacity: imageBlocked ? 0.55 : 1,
                      border: 2,
                      borderColor: isSelected ? 'primary.main' : 'transparent',
                      transition: 'all 0.2s',
                      '&:hover': { 
                        borderColor: imageBlocked ? 'transparent' : 'primary.light',
                        elevation: 3
                      }
                    }}
                  >
                    {option.imageUrl && (
                      <Box
                        component="img"
                        src={option.imageUrl.startsWith('/') ? `${SERVER_BASE}${option.imageUrl}` : option.imageUrl}
                        alt={option.text}
                        sx={{ 
                          width: '100%', 
                          maxHeight: 200, 
                          objectFit: 'contain', 
                          borderRadius: 1, 
                          mb: 1 
                        }}
                      />
                    )}
                    <Typography variant="body2" align="center">
                      {option.text}
                    </Typography>
                  </Paper>
                );
              })}
            </Box>
            {question.allowCustomOption && (
              <FormControlLabel
                control={
                    <Checkbox
                      size="small"
                      checked={hasCustomImg}
                      disabled={!hasCustomImg && atImageSelectionCap}
                      onChange={(e) => {
                        if (e.target.checked && isSelectionCapped(question, answerArrayImg)) return;
                        if (e.target.checked) {
                          // Add custom marker
                          const validOptions = answerArrayImg.filter(a => imageOptionsMultiple.find(opt => opt.text === a));
                          handleAnswerChange(question.id, [...validOptions, '__custom__']);
                        } else {
                        // Remove all custom values
                        const newAnswer = answerArrayImg.filter(a => imageOptionsMultiple.find(opt => opt.text === a));
                        handleAnswerChange(question.id, newAnswer);
                      }
                    }}
                  />
                }
                label={<Typography variant="body2">Свой вариант</Typography>}
              />
            )}
            {question.allowCustomOption && hasCustomImg && (
              <TextField
                fullWidth
                size="small"
                placeholder="Введите свой вариант"
                value={customValuesImg.filter(v => v !== '__custom__').join(', ')}
                onChange={(e) => {
                  const validOptions = answerArrayImg.filter(a => imageOptionsMultiple.find(opt => opt.text === a));
                  if (e.target.value.trim()) {
                    handleAnswerChange(question.id, [...validOptions, e.target.value]);
                  } else {
                    handleAnswerChange(question.id, [...validOptions, '__custom__']);
                  }
                }}
                sx={{ mt: 1 }}
              />
            )}
          </Box>
        );
      
      case 'likert':
        const likertOptions = question.likertOptions || [
          'Полностью не согласен',
          'Скорее не согласен',
          'Нейтрально',
          'Скорее согласен',
          'Полностью согласен'
        ];
        return (
          <RadioGroup
            value={answer || ''}
            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
          >
            {likertOptions.map((option, idx) => (
              <FormControlLabel
                key={idx}
                value={option}
                control={<Radio />}
                label={option}
              />
            ))}
          </RadioGroup>
        );
      
      case 'ranking':
        if (!question.options || question.options.length === 0) {
          return (
            <Alert severity="info" variant="outlined">
              <Typography variant="caption">Добавьте варианты для ранжирования</Typography>
            </Alert>
          );
        }
        
        const rankingAnswers = Array.isArray(answer) ? answer : [];
        const availableOptions = (question.options || []).filter(opt => !rankingAnswers.includes(opt));
        
        return (
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Расставьте варианты в порядке предпочтения (нажмите чтобы добавить):
            </Typography>
            
            {/* Selected items in order */}
            {rankingAnswers.length > 0 && (
              <Box sx={{ mb: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                {rankingAnswers.map((option, idx) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Chip label={idx + 1} size="small" color="primary" sx={{ minWidth: 32 }} />
                    <Typography variant="body2" sx={{ flex: 1 }}>{option}</Typography>
                    <IconButton
                      size="small"
                      onClick={() => {
                        const newRanking = [...rankingAnswers];
                        newRanking.splice(idx, 1);
                        handleAnswerChange(question.id, newRanking);
                      }}
                      sx={{ color: 'error.main' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
            
            {/* Available options to add */}
            {availableOptions.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  Доступные варианты:
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {availableOptions.map((opt, idx) => (
                    <Button
                      key={idx}
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        handleAnswerChange(question.id, [...rankingAnswers, opt]);
                      }}
                      sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                    >
                      {opt}
                    </Button>
                  ))}
                </Box>
              </Box>
            )}
            
            {rankingAnswers.length === 0 && availableOptions.length === 0 && (
              <Alert severity="info" variant="outlined">
                <Typography variant="caption">Нет вариантов для ранжирования</Typography>
              </Alert>
            )}
          </Box>
        );
      
      case 'matrix':
        if (!question.matrixRows || question.matrixRows.length === 0 || 
            !(question.matrixColumns || question.matrixCols) || 
            ((question.matrixColumns || question.matrixCols) || []).length === 0) {
          return (
            <Alert severity="info" variant="outlined">
              <Typography variant="caption">Настройте строки и колонки матрицы</Typography>
            </Alert>
          );
        }
        
        const matrixAnswers = answer || {};
        const matrixColumns = question.matrixColumns || question.matrixCols || [];
        const missingMatrixRows = attemptedNext && matrixBlocksProgress(question, answer)
          ? new Set(getMissingMatrixRows(question, answer))
          : new Set();
        
        return (
          <MatrixQuestion
            rows={question.matrixRows || []}
            columns={matrixColumns}
            value={matrixAnswers}
            missingRows={missingMatrixRows}
            onChange={(next) => handleAnswerChange(question.id, next)}
          />
        );
      
      case 'scale':
        const scaleMin = question.scaleMin || 1;
        const scaleMax = question.scaleMax || 10;
        const scaleValue = answer !== undefined ? answer : scaleMin;
        
        return (
          <Box sx={{ px: 1 }}>
            <Slider
              value={scaleValue}
              onChange={(e, val) => handleAnswerChange(question.id, val)}
              min={scaleMin}
              max={scaleMax}
              step={1}
              marks
              valueLabelDisplay="on"
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {question.scaleMinLabel || scaleMin}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {question.scaleMaxLabel || scaleMax}
              </Typography>
            </Box>
          </Box>
        );
      
      case 'nps':
        const npsValue = answer !== undefined ? answer : null;
        return (
          <Box>
            <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                <Button
                  key={num}
                  size="small"
                  variant={npsValue === num ? 'contained' : 'outlined'}
                  onClick={() => handleAnswerChange(question.id, num)}
                  sx={{ minWidth: 35, p: 0.5 }}
                >
                  {num}
                </Button>
              ))}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" color="text.secondary">
                Не рекомендую
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Рекомендую
              </Typography>
            </Box>
          </Box>
        );
      
      case 'rating':
        const ratingValue = answer || 0;
        const maxRating = question.ratingMax || 5;
        
        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {[...Array(maxRating)].map((_, idx) => (
              <IconButton
                key={idx}
                size="small"
                onClick={() => handleAnswerChange(question.id, idx + 1)}
                sx={{ 
                  color: idx < ratingValue ? 'warning.main' : 'action.disabled',
                  fontSize: '2rem'
                }}
              >
                ★
              </IconButton>
            ))}
          </Box>
        );
      
      case 'file':
        return (
          <Box>
            <input
              accept={question.fileTypes || '*/*'}
              style={{ display: 'none' }}
              id={`file-upload-${question.id}`}
              type="file"
              onChange={(e) => handleFileUpload(question.id, e.target.files[0])}
            />
            <label htmlFor={`file-upload-${question.id}`}>
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadIcon />}
              >
                Загрузить файл
              </Button>
            </label>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Максимальный размер файла: 15МБ
            </Typography>
            {files[question.id] && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Выбран файл: {files[question.id].name} ({(files[question.id].size / 1024 / 1024).toFixed(2)} МБ)
              </Typography>
            )}
          </Box>
        );
      
      default:
        return (
          <Alert severity="warning">
            Неподдерживаемый тип вопроса: {question.type}
          </Alert>
        );
    }
  };
  
  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Typography>Загрузка опроса...</Typography>
        </Paper>
      </Container>
    );
  }
  
  if (!survey) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Alert severity="error">{error || 'Опрос не найден'}</Alert>
          <Button onClick={() => navigate('/respondent/dashboard')} sx={{ mt: 2 }}>
            Вернуться к дашборду
          </Button>
        </Paper>
      </Container>
    );
  }
  
  const currentPage = getCurrentPage();
  const questions = getCurrentPageQuestions();
  const progress = survey.pages.length > 0 
    ? ((currentPageIndex + 1) / survey.pages.length) * 100 
    : 0;
  
  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper 
        elevation={0}
        sx={{ 
          p: { xs: 1.5, md: 4 },
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: { xs: '8px', md: '32px' },
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        }}
      >
        {/* Header */}
        <Typography 
          variant="h5" 
          gutterBottom
          sx={{
            fontWeight: 700,
            color: '#2d3748'
          }}
        >
          {survey.title}
        </Typography>
        
        {/* Progress - only show if more than one page */}
        {survey.pages.length > 1 && (
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
              <Typography 
                variant="body2" 
                sx={{
                  color: '#0984e3',
                  fontWeight: 700
                }}
              >
                {Math.round(progress)}%
              </Typography>
            </Box>
            <Box
              sx={{
                width: '100%',
                height: 8,
                background: 'rgba(116, 185, 255, 0.2)',
                borderRadius: '8px',
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  width: `${progress}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #74b9ff 0%, #0984e3 100%)',
                  transition: 'width 0.3s ease',
                  boxShadow: '0 2px 8px rgba(9, 132, 227, 0.4)'
                }}
              />
            </Box>
          </Box>
        )}
        
        {/* Questions */}
        {questions.map((question, idx) => {
          const rangeMessage = selectionProgressMessage(question, answers[question.id]) || matrixProgressMessage(question, answers[question.id]);
          const hint = selectionHint(question);
          const showError = attemptedNext && isQuestionUnanswered(question);
          return (
            <Card 
              key={question.id} 
              id={`question-${question.id}`}
              elevation={0}
              sx={{ 
                mb: 3,
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                borderRadius: { xs: '16px', md: '20px' },
                border: '1px solid',
                borderColor: showError ? '#d63031' : 'rgba(255, 255, 255, 0.3)',
                boxShadow: showError 
                  ? '0 4px 20px rgba(214, 48, 49, 0.2)'
                  : '0 4px 20px rgba(0, 0, 0, 0.08)',
                transition: 'all 0.3s ease'
              }}
            >
              <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                <Typography 
                  variant="subtitle1" 
                  gutterBottom
                  sx={{
                    fontWeight: 700,
                    color: '#2d3748',
                    fontSize: '1.1rem'
                  }}
                >
                  {question.text}
                  {question.required && (
                    <Typography component="span" sx={{ color: '#d63031' }}> *</Typography>
                  )}
                </Typography>
                {hint && (
                  <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>
                    {hint}
                  </Typography>
                )}
                {showError && (
                  <Alert 
                    severity="error" 
                    sx={{ 
                      mt: 1.5,
                      mb: 2,
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, rgba(255, 118, 117, 0.1), rgba(214, 48, 49, 0.1))',
                      border: '1px solid rgba(214, 48, 49, 0.3)',
                      '& .MuiAlert-icon': { 
                        color: '#d63031' 
                      }
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#d63031',
                        fontWeight: 600
                      }}
                    >
                      {rangeMessage || 'На этот вопрос обязательно нужно ответить'}
                    </Typography>
                  </Alert>
                )}
                {renderQuestion(question)}
              </CardContent>
            </Card>
          );
        })}
        
        {/* Navigation */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, gap: 2 }}>
          <Button
            onClick={handleBack}
            disabled={pageHistory.length <= 1 || submitting}
            sx={{
              color: '#718096',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '12px',
              px: 4,
              py: 1.2,
              border: '1px solid rgba(0, 0, 0, 0.1)',
              '&:hover': {
                background: 'rgba(0, 0, 0, 0.04)',
                border: '1px solid rgba(0, 0, 0, 0.2)'
              },
              '&.Mui-disabled': {
                color: 'rgba(0, 0, 0, 0.26)',
                border: '1px solid rgba(0, 0, 0, 0.05)'
              }
            }}
          >
            Назад
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={submitting}
            sx={{
              background: currentPageIndex >= survey.pages.length - 1
                ? 'linear-gradient(135deg, #56ab2f 0%, #2d7a1f 100%)'
                : 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
              color: 'white',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '12px',
              px: 4,
              py: 1.2,
              boxShadow: currentPageIndex >= survey.pages.length - 1
                ? '0 4px 20px rgba(86, 171, 47, 0.4)'
                : '0 4px 20px rgba(9, 132, 227, 0.4)',
              '&:hover': {
                background: currentPageIndex >= survey.pages.length - 1
                  ? 'linear-gradient(135deg, #2d7a1f 0%, #56ab2f 100%)'
                  : 'linear-gradient(135deg, #0984e3 0%, #74b9ff 100%)'
              },
              '&.Mui-disabled': {
                background: 'rgba(0, 0, 0, 0.12)',
                color: 'rgba(0, 0, 0, 0.26)'
              }
            }}
          >
            {currentPageIndex >= survey.pages.length - 1
              ? (submitting ? 'Отправка...' : 'Завершить')
              : 'Далее'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}
